import { Box, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material"
import BannerReportTIM from "./BannerReportTIM"
import { useContext, useEffect } from "react"
import { getReportInfo360AssessmentAll } from "../axios/axiosFunctions"
import { UserContext } from "../contexts/user"
import { useState } from "react"
import { BarChart, axisClasses, chartsGridClasses } from "@mui/x-charts"

const BlockReport360Intro = ({slug,group,onSectionsDataReady,onSummaryScoresReady,onResponseRateReady,reportUserId}) => {

    const {currentUser} = useContext(UserContext);
    const [infoChart, setInfoChart] = useState([]);
    const [summaryInfo, setSummaryInfo] = useState([]);
    const [responseRate, setResponseRate] = useState(null);

    // Use reportUserId (admin preview) if available, otherwise currentUser
    const effectiveUserId = reportUserId || currentUser._id;

    useEffect(() => {
        const callInfo = async()=>{
            if (!group?._id) return;
            const res = await getReportInfo360AssessmentAll(slug,effectiveUserId,group._id);
            if(res.responseRate){
                setResponseRate(res.responseRate);
                onResponseRateReady?.(res.responseRate);
            }
            const infoS = [res.data.pop()];
            const info = res.data.map(b=>{
                const pct = Number(b.positivePercentage);
                const ratingColor = pct >= 90 ? "#69AF45" : pct >= 60 ? "#2B8DCC" : "#e67e22";
                return {
                    name: b.name,
                    positive: pct,
                    color: ratingColor,
                    blockId: b.blockId
                };
            });
            setInfoChart(info);
            // Pass raw sections data to parent for PDF (no image needed)
            if (onSectionsDataReady) {
                onSectionsDataReady(info);
            }
            onSummaryScoresReady?.(info);
            const data2 = infoS.map(info=>({
                positive: Number(info.positivePercentage),
                name:info.name
            }))
            setSummaryInfo(data2);
        }
        callInfo();
    }, []);

    const handleLabels = (item)=>{
        if(item.value < 60)
            return "OPPORTUNITY";
        if(item.value >= 60 && item.value <90)
            return "DEVELOPING";
        if(item.value >= 90)
            return "STRENGTH";
        return `${item.value}%`
    }

    const ZeroBarOverlay = () => (
        <Typography
            sx={{
                position: "absolute",
                top: "50%",
                left: 258,
                transform: "translateY(-30%)",
                color: "#e67e22",
                fontSize: 13,
                fontWeight: 600,
                pointerEvents: "none",
            }}
        >
            OPPORTUNITY (0%)
        </Typography>
    );

    return (
    <>
        <BannerReportTIM text={slug === "lim_360_assessment" ? "Leaders in Motion 360-Degree Summary" : "Managers in Motion 360-Degree Summary"}/>
        <Box display="flex" flexDirection="column" sx={{padding:{xs:"0px 16px",sm:"0px 24px",md:"0px 48px"}}}>
            <Grid container spacing={2} sx={{margin:"32px 0px 40px 0px"}}>
                <Grid size={12}>
                    <Typography variant="body1" color="secondary" fontWeight={400} sx={{lineHeight:"1.7",fontSize:"1.05rem"}}>Your report reflects your confidence level on the {slug === "lim_360_assessment" ? "six" : "four"} dimensions.  Each dimension had {slug === "lim_360_assessment" ? "10" : "10"} related questions.  To calculate your percentage score in each dimension, we counted the number responses that were, "Very Confident" or "Confident," and then calculated the percentage.  We use these two answers to represent "Positive Responses".</Typography>
                    <Typography variant="body1" color="secondary" fontWeight={400} sx={{lineHeight:"1.7",fontSize:"1.05rem",marginTop:"12px"}}>For developmental purposes, we categorize your score into three different levels to help you focus your development priorities.</Typography>
                    <Box display={"flex"} justifyContent={"center"}>
                        <TableContainer sx={{maxWidth:"550px",margin:"32px 0px",borderRadius:"8px",overflow:"hidden",boxShadow:"0 1px 4px rgba(15,83,133,0.08)"}}>
                            <Table sx={{borderCollapse:'separate',borderSpacing:0,'& .MuiTableCell-root':{border:'none',padding:'12px 20px',fontSize:'0.95rem'}}}>
                                <TableHead>
                                    <TableRow sx={{backgroundColor:"#0F5385"}}>
                                        <TableCell sx={{fontWeight:"600",color:"white"}}>Score (% of positive responses)</TableCell>
                                        <TableCell sx={{fontWeight:"600",color:"white"}}>Rating</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow sx={{backgroundColor:"#f0f7f0"}}>
                                        <TableCell>90% or higher</TableCell>
                                        <TableCell sx={{fontWeight:600,color:"#69AF45"}}>STRENGTH</TableCell>
                                    </TableRow>
                                    <TableRow sx={{backgroundColor:"#f5f9fc"}}>
                                        <TableCell>60%–89%</TableCell>
                                        <TableCell sx={{fontWeight:600,color:"#2B8DCC"}}>DEVELOPING</TableCell>
                                    </TableRow>
                                    <TableRow sx={{backgroundColor:"#fef8f0"}}>
                                        <TableCell>Less than 60%</TableCell>
                                        <TableCell sx={{fontWeight:600,color:"#e67e22"}}>OPPORTUNITY</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Grid>
                {responseRate && (
                    <Grid size={12}>
                        <Typography variant="h6" color="secondary" fontWeight={600} sx={{marginTop:"20px"}}>Response Rate ({responseRate.totalInvited > 0 ? Math.round((responseRate.totalResponded / responseRate.totalInvited) * 100) : 0}%)</Typography>
                        <Typography variant="body1" color="secondary" fontWeight={400} sx={{fontSize:"1.05rem"}}>You invited {responseRate.totalInvited} people to complete the survey. {responseRate.totalResponded} people responded.</Typography>
                    </Grid>
                )}
                <Grid size={12}>
                    <Box display={"flex"} flexDirection={"column"} alignItems={"center"} sx={{marginBottom:"8px"}}>
                        <Typography variant="h6" fontWeight={600} color="secondary">BLOCK SUMMARY SCORES</Typography>
                        <Typography variant="body1" color="secondary" fontWeight={400} sx={{fontSize:"1.05rem", marginTop:"8px"}}>Here is a summary of your scores on each of the four blocks.</Typography>
                    </Box>
                    {
                        infoChart.map((b,i)=>(
                            <Box key={i} sx={{ position: "relative" }}>
                                <BarChart
                                    dataset={[b]}
                                    xAxis={[{min:0,max:100,tickNumber:5,tickLabelStyle:{fontSize:11},position:"top"}]}
                                    yAxis={[{scaleType:"band",dataKey:"name",width:250,tickLabelStyle:{fontSize:13,fontWeight:400}}]}
                                    series={[{dataKey:"positive",valueFormatter: (value) => `${value}%`,color:b.color}]}
                                    layout="horizontal"
                                    grid={{vertical:true}}
                                    height={120}
                                    margin={{right:40,top:30,bottom:20}}
                                    barLabel={handleLabels}
                                    sx={{
                                        [`& .${axisClasses.tickLabel}`]: {
                                        whiteSpace: 'pre-wrap',
                                        overflow: 'visible',
                                        textOverflow: 'unset',
                                        },
                                        [`& .${chartsGridClasses.line}`]: { strokeWidth: 2 },
                                        "& .MuiBarLabel-root": {
                                            fill: "white",
                                            fontSize: 13,
                                            fontWeight: 600,
                                        },
                                    }}
                                />
                                {b.positive === 0 && <ZeroBarOverlay />}
                            </Box>
                        ))
                    }
                    <Typography variant="body1" color="secondary" fontWeight={400} sx={{lineHeight:"1.7",fontSize:"1.05rem"}}>In the pages that follow, you can review your scores, question by question, to help you plan your development priorities.</Typography>
                </Grid>
            </Grid>
        </Box>
    </>
    )
}

export default BlockReport360Intro